/** Shared brand hex helpers for Circa org theming. */

export const BRAND_HEX_RE = /^#[0-9A-Fa-f]{6}$/;

/** Circa theme defaults (from :root HSL) shown in Org Settings when fields are empty. */
export const CIRCA_BRAND_DEFAULTS = {
  brand_primary: "#E8892D",
  brand_secondary: "#403930",
  brand_accent: "#E8892D",
};

export function isBrandHex(value) {
  return typeof value === "string" && BRAND_HEX_RE.test(value.trim());
}

export function normalizeBrandHexInput(value) {
  if (value == null) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  if (!BRAND_HEX_RE.test(trimmed)) return null;
  return trimmed.toUpperCase();
}

/** Parse #RRGGBB → { r, g, b } 0–255 */
export function hexToRgb(hex) {
  const h = normalizeBrandHexInput(hex);
  if (!h) return null;
  return {
    r: parseInt(h.slice(1, 3), 16),
    g: parseInt(h.slice(3, 5), 16),
    b: parseInt(h.slice(5, 7), 16),
  };
}

/**
 * Convert #RRGGBB to Tailwind/shadcn HSL channel string: "H S% L%"
 * (without hsl() wrapper — matches CSS var usage).
 */
export function hexToHslChannels(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  let { r, g, b } = rgb;
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      default:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }
  const H = Math.round(h * 360);
  const S = Math.round(s * 100);
  const L = Math.round(l * 100);
  return `${H} ${S}% ${L}%`;
}

/** Relative luminance 0–1 for contrast decisions. */
export function relativeLuminance(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  const lin = [rgb.r, rgb.g, rgb.b].map((c) => {
    const x = c / 255;
    return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
}

/** Dark foreground for light primaries; light for dark primaries. */
export function contrastingForegroundChannels(hex) {
  return relativeLuminance(hex) > 0.45 ? "24 30% 8%" : "36 30% 98%";
}

/**
 * Soften a brand color for dark chrome / accents.
 * Keeps hue; optionally clamps saturation so neon brands stay readable on dark UI.
 */
export function softSurfaceChannels(
  hex,
  { lightness = 14, maxSaturation = null } = {}
) {
  const channels = hexToHslChannels(hex);
  if (!channels) return null;
  const [h, sRaw] = channels.split(" ");
  let s = parseInt(String(sRaw).replace("%", ""), 10);
  if (Number.isNaN(s)) s = 0;
  if (maxSaturation != null) s = Math.min(s, maxSaturation);
  return `${h} ${s}% ${lightness}%`;
}

/** Chrome surfaces use capped saturation so brand hue tints without neon wash. */
const CHROME_MAX_SAT = 38;

export const BRAND_CSS_VARS = [
  "--primary",
  "--primary-foreground",
  "--ring",
  "--sidebar-primary",
  "--sidebar-primary-foreground",
  "--sidebar-ring",
  "--sidebar-accent",
  "--sidebar-background",
  "--sidebar-border",
  "--brand-secondary",
  "--circa-orbit",
  "--circa-orbit-soft",
  "--circa-glow",
  "--background",
  "--card",
  "--popover",
  "--muted",
  "--secondary",
  "--secondary-foreground",
  "--accent",
  "--border",
  "--input",
  // Readable ink on light community / social surfaces (optional; org dark chrome omits these)
  "--foreground",
  "--card-foreground",
  "--popover-foreground",
  "--muted-foreground",
];

/**
 * Tint dark Circa chrome from a brand seed (hue preserved, sat capped, L ladder).
 */
function applyChromeSurfaces(vars, seedHex) {
  const chrome = (lightness) =>
    softSurfaceChannels(seedHex, {
      lightness,
      maxSaturation: CHROME_MAX_SAT,
    });
  const bg = chrome(9);
  const sidebarBg = chrome(10);
  const card = chrome(11);
  const popover = chrome(12);
  const muted = chrome(15);
  const secondary = chrome(18);
  const accentSurface = chrome(19);
  const border = chrome(20);
  if (bg) vars["--background"] = bg;
  if (sidebarBg) vars["--sidebar-background"] = sidebarBg;
  if (card) vars["--card"] = card;
  if (popover) vars["--popover"] = popover;
  if (muted) vars["--muted"] = muted;
  if (secondary) vars["--secondary"] = secondary;
  if (accentSurface) vars["--accent"] = accentSurface;
  if (border) {
    vars["--border"] = border;
    vars["--input"] = border;
    vars["--sidebar-border"] = border;
  }
}

/**
 * Build CSS custom property map from org brand hex trio.
 * Missing fields leave those tokens unset (caller clears all first).
 */
export function brandCssVarsFromOrg(org) {
  if (!org) return {};
  const vars = {};
  const primary = normalizeBrandHexInput(org.brand_primary);
  const secondary = normalizeBrandHexInput(org.brand_secondary);
  const accent = normalizeBrandHexInput(org.brand_accent);

  if (primary) {
    const p = hexToHslChannels(primary);
    const fg = contrastingForegroundChannels(primary);
    vars["--primary"] = p;
    vars["--primary-foreground"] = fg;
    vars["--ring"] = p;
    vars["--sidebar-primary"] = p;
    vars["--sidebar-primary-foreground"] = fg;
    vars["--sidebar-ring"] = p;
  }
  if (accent) {
    const a = hexToHslChannels(accent);
    vars["--circa-orbit"] = a;
    vars["--circa-glow"] = a;
    const soft = softSurfaceChannels(accent, { lightness: 42 });
    if (soft) vars["--circa-orbit-soft"] = soft;
  }

  const surfaceSeed = primary || secondary;
  if (surfaceSeed) {
    applyChromeSurfaces(vars, surfaceSeed);
    const sidebarAccentSeed = secondary || primary;
    const sidebarAccent = softSurfaceChannels(sidebarAccentSeed, {
      lightness: 17,
      maxSaturation: CHROME_MAX_SAT,
    });
    if (sidebarAccent) vars["--sidebar-accent"] = sidebarAccent;
  }

  if (secondary) {
    const s = hexToHslChannels(secondary);
    vars["--brand-secondary"] = s;
    // Secondary buttons/badges use semantic --secondary
    vars["--secondary"] = s;
    vars["--secondary-foreground"] = contrastingForegroundChannels(secondary);
  }

  return vars;
}

/**
 * Whether OrgBrandThemeProvider should write CSS vars for the selection.
 */
export function shouldApplyOrgBrandTheme({
  selectedOrgId,
  useOrgBranding,
  org,
}) {
  if (!selectedOrgId || selectedOrgId === "social") return false;
  if (useOrgBranding === false) return false;
  return Object.keys(brandCssVarsFromOrg(org)).length > 0;
}

export function applyBrandCssVars(el, vars) {
  if (!el) return;
  for (const key of BRAND_CSS_VARS) {
    el.style.removeProperty(key);
  }
  for (const [key, value] of Object.entries(vars || {})) {
    if (value) el.style.setProperty(key, value);
  }
}

export function clearBrandCssVars(el) {
  applyBrandCssVars(el, {});
}
